import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { JobStatus } from '../../../../../enums/job-status';
import {
  ButtonModule,
  CheckboxModule,
  DialogModule,
  DropdownModule,
  InputTextareaModule,
  InputTextModule,
  MessageService,
  MultiSelectModule,
  SelectItem,
} from 'primeng';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { NgIf } from '@angular/common';
import {
  JobScreenOrdersDepartment,
  JobScreenOrdersJob,
  JobScreenOrdersOrder,
  JobScreenOrdersTeam,
} from '../../../../../interfaces/screen-specific/job-screen';
import { TaskType } from '../../../../../enums/task.type';
import { Message } from '../../../../../classes/Message';
import {
  ICancellationStatus,
  ISecondaryCancellationStatus,
} from '../../../../../interfaces/cancellation-status';
import {
  AccountCodeService,
  EventService,
  JobsService,
  OrderService,
  OrderTagService,
  OrganisationsService,
  UserService,
} from '../../../../../services';
import { Router } from '@angular/router';
import { JobScreenCommonService } from '../../job-screen-common.service';
import { InputHelper } from '../../../../../utils/input.helper';
import { ControlsOf } from '../../../../../utils/controls-of';

const defaultSelectItem: SelectItem = {
  label: 'Please Select',
  value: null,
};
@Component({
  selector: 'vme-edit-order-dialog',
  templateUrl: './vme-edit-order-dialog.component.html',
  styleUrls: ['./vme-edit-order-dialog.component.scss'],
  standalone: true,
  imports: [
    DropdownModule,
    DialogModule,
    FormsModule,
    InputTextModule,
    NgIf,
    MultiSelectModule,
    CheckboxModule,
    InputTextareaModule,
    ButtonModule,
    ReactiveFormsModule,
  ],
})
export class VmeEditOrderDialogComponent implements OnInit {
  loading: boolean;
  cancellationDescription: string;
  purchaseOrderNo: string;
  accountCodes: SelectItem[];
  orderTags: SelectItem[];
  cancellationStatusOptions: SelectItem[];
  constructor(
    private jobService: JobsService,
    private accountCodeService: AccountCodeService,
    private orgService: OrganisationsService,
    private orderTagService: OrderTagService,
    public messageService: MessageService,
    private orderService: OrderService,
    public inputHelper: InputHelper,
  ) {}

  ngOnInit(): void {}

  protected readonly jobStatus = JobStatus;
  @Input() visible: boolean = false;
  @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input() selectedOrder: JobScreenOrdersOrder;
  @Input('organisationTypeId') organisationTypeId!: number;

  @Input() job: JobScreenOrdersJob;

  @Output() onSaveEditOrder = new EventEmitter<JobScreenOrdersJob>();

  cancellationStatusId: number;

  cancellationStatuses: ICancellationStatus[] = [];
  customerDepartments: any[];
  orderCustomerPrice: number;
  orderInspectionCharge: number;
  orderShowInspectionCharge: boolean;

  selectedOrderTeam: JobScreenOrdersTeam;
  selectedOrderDepartment: JobScreenOrdersDepartment;
  departmentTeams: JobScreenOrdersTeam[];
  selectedAccountCode: number;
  searchingAccountCodes: boolean;

  selectedOrderTags: number[];
  searchingTags: boolean;

  isBusyEditOrder: boolean;
  searchOrderTags: boolean;
  searchingOrderDepartments: boolean;
  isCustomerInvoiced: boolean;
  searchingDepartmentTeams: boolean;
  displaySecondaryCancellation: boolean = false;
  secondaryCancellationStatusOptions: SelectItem[] = [];
  secondaryCancellationStatusId: number;

  isOverridePrice: boolean;

  async onShow() {
    this.loading = true;
    await this.showEditOrderDialog(this.selectedOrder);
    this.loading = false;
  }

  async showEditOrderDialog(selectedOrder: JobScreenOrdersOrder) {
    if (!selectedOrder) {
      return;
    }

    await this.populateCancellationStatuses();

    this.cancellationStatusId = selectedOrder.cancellationStatusId;
    this.secondaryCancellationStatusId = selectedOrder.subCancellationStatusId;
    this.onCancellationStatusChange();

    this.orderService.getFullCancellationDescription(selectedOrder.id).then((res) => {
      this.cancellationDescription = res.fullDescription;
    });

    this.orderCustomerPrice = selectedOrder.customerPrice;
    this.isOverridePrice = selectedOrder.isOverridePrice;
    this.purchaseOrderNo = selectedOrder.purchaseOrderNo;
    this.orderInspectionCharge = selectedOrder.inspectionCharge;
    this.orderCustomerPrice = selectedOrder.customerPrice;
    this.selectedOrderTeam = selectedOrder.customerTeam;
    this.cancellationDescription = selectedOrder.cancellationDescription;

    this.selectedAccountCode = selectedOrder.accountCode?.id;
    this.accountCodes = [];

    this.orderTags = [];
    this.selectedOrderTags = [];

    this.orderShowInspectionCharge = [
      TaskType.DrivenPlatedInspection,
      TaskType.DrivenInsuredInspection,
    ].includes(selectedOrder.taskTypeId);

    this.loadAccountCodes(selectedOrder.customer.id, selectedOrder);

    this.loadOrderTags(selectedOrder.customer.id, selectedOrder);

    //get selected order's department
    this.loadDepartments();
  }

  async initCancellationStatuses() {
    this.cancellationStatuses = [];

    const res = await this.orderService.getCancellationStatuses();
    res.forEach((x) => {
      this.cancellationStatuses.push({
        description: x.description,
        id: x.id,
        isActive: x.isActive,
        name: x.name,
        secondaryCancellationStatuses: x.secondaryCancellationStatuses,
      });
    });
  }

  loadOrderTags(customerId: number, selectedOrder: JobScreenOrdersOrder) {
    if (customerId == null || selectedOrder == null) {
      return;
    }

    this.searchingTags = true;
    this.orderTagService
      .getForCustomer(customerId)
      .then((result) => {
        this.searchingTags = false;
        const selectedOrderTags = [];
        const items: SelectItem[] = [];
        if (result != null) {
          result.forEach((x) => {
            items.push({ label: x.description, value: x.id });
          });

          if (selectedOrder.orderTags != null) {
            items.forEach((x) => {
              selectedOrder.orderTags.forEach((y) => {
                if (x.value != null && x.value == y.orderTag.id) {
                  selectedOrderTags.push(x.value);
                }
              });
            });
          }
        }

        this.orderTags = items;
        this.selectedOrderTags = selectedOrderTags;
      })
      .catch(() => {
        this.searchingTags = false;
        this.orderTags = [];
        this.selectedOrderTags = [];
        this.messageService.add(
          Message.Error(
            'An error occurred while loading Job Tags, please try again.',
            'Load Job Tags',
          ),
        );
      });
  }

  loadDepartments() {
    this.orgService.getOrg(this.selectedOrder.customer.id).then((org) => {
      const allDepartments = org.departments;
      this.customerDepartments = [];
      allDepartments.sort(this.compareItem);
      this.customerDepartments = allDepartments;
      this.selectedOrderDepartment = this.selectedOrder.customerDepartment;
      void this.loadTeams(this.selectedOrder.customerDepartment.id);
    });
  }

  compareItem(x, y) {
    if (x.name.toLowerCase() > y.name.toLowerCase()) return 1;
    else if (x < y) return -1;
    else return 0;
  }

  departmentChange(event) {
    this.selectedOrderTeam = null;
    void this.loadTeams(event.value.id);
  }

  loadTeams(departmentId: number): Promise<any> {
    let promise = null;
    this.departmentTeams = [];
    if (departmentId != null) {
      promise = this.orgService.getDepartment(departmentId).then((data) => {
        if (data != null) {
          const allTeams = data.teams;
          allTeams.sort(this.compareItem);
          this.departmentTeams = allTeams;
          this.selectedOrderTeam = this.selectedOrder.customerTeam;
        }
      });
    }
    return promise;
  }

  loadAccountCodes(customerId: number, order: JobScreenOrdersOrder) {
    if (customerId == null || order == null) {
      return;
    }
    this.searchingAccountCodes = true;
    this.accountCodeService
      .getForSupplier(customerId)
      .then((result) => {
        let selectAccountCode = null;
        this.searchingAccountCodes = false;
        const items: SelectItem[] = [];
        items.push({ label: 'Please Select', value: null });
        if (result != null) {
          result.forEach((x) => {
            items.push({ label: x.name, value: x.id });
          });

          if (order.accountCode != null) {
            items.forEach((x) => {
              if (x.value != null && x.value == order.accountCode.id) {
                selectAccountCode = x.value;
              }
            });
          }
        }

        this.accountCodes = items;
        this.selectedAccountCode = selectAccountCode;
      })
      .catch(() => {
        this.searchingAccountCodes = false;
        this.accountCodes = [];
        this.selectedAccountCode = null;
        this.messageService.add(
          Message.Error(
            'An error occurred while loading Account Code, please try again.',
            'Load Account Codes',
          ),
        );
      });
  }

  protected saveEditOrder() {
    this.isBusyEditOrder = true;
    this.jobService
      .changeOrderDetail({
        orderId: this.selectedOrder.id,
        accountCodeId: this.selectedAccountCode != null ? this.selectedAccountCode : null,
        customerPrice: this.orderCustomerPrice,
        selectedTagIds: this.selectedOrderTags,
        isOverridePrice: this.isOverridePrice,
        purchaseOrderNo: this.purchaseOrderNo,
        cancellationStatusId: this.cancellationStatusId,
        secondaryCancellationStatusId: this.secondaryCancellationStatusId,
        cancellationDescription: this.cancellationDescription,
        customerDepartmentId: this.selectedOrderDepartment?.id,
        customerTeamId: this.selectedOrderTeam?.id,
        inspectionCharge: this.orderInspectionCharge,
      })
      .then(() => {
        this.isBusyEditOrder = false;
        this.messageService.add(Message.Success('Changes were successful.', 'Edit order'));
        this.onSaveEditOrder.emit(this.job);
      })
      .catch((reason) => {
        console.log(reason);
        this.isBusyEditOrder = false;
        this.messageService.add(
          Message.Error(
            'An error occurred while trying to modify the data. Try again. If the error persists, contact your administrator.',
            'Edit order',
          ),
        );
      })
      .finally(() => {
        this.visibleChange.emit(false);
      });
  }

  onCancellationStatusChange() {
    if (!this.selectedCancellationStatus) {
      this.hideSecondaryCancellationInput();
      return;
    }

    const secondaryStatuses = this.selectedCancellationStatus.secondaryCancellationStatuses.filter(
      (x) => x.isActive,
    );

    if (secondaryStatuses.length == 0) {
      this.hideSecondaryCancellationInput();
      return;
    }

    this.secondaryCancellationStatusOptions = [defaultSelectItem].concat(
      secondaryStatuses.map((x) => ({ label: x.name, value: x.id })),
    );

    this.displaySecondaryCancellation = true;
  }

  hideSecondaryCancellationInput() {
    this.displaySecondaryCancellation = false;
    this.secondaryCancellationStatusId = null;
  }

  public get selectedCancellationStatus(): ICancellationStatus {
    return this.cancellationStatuses.find((c) => c.id === this.cancellationStatusId);
  }

  public get selectedSecondaryCancellationStatus(): ISecondaryCancellationStatus {
    return this.selectedCancellationStatus.secondaryCancellationStatuses.find(
      (c) => c.id === this.secondaryCancellationStatusId,
    );
  }

  async populateCancellationStatuses(): Promise<void> {
    const result = await this.orderService.getCancellationStatuses();
    this.cancellationStatuses = result.filter((x) => x.isActive);
    this.cancellationStatusOptions = [defaultSelectItem].concat(
      this.cancellationStatuses.map((x) => ({
        description: x.description,
        label: x.name,
        isActive: x.isActive,
        value: x.id,
        secondaryCancellationStatuses: x.secondaryCancellationStatuses,
      })),
    );
  }

  protected onCancel() {
    this.visible = false;
    this.visibleChange.emit(false);
  }
}
